import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Send, 
    Clock, 
    User, 
    Tag,
    CheckCircle2,
    XCircle,
    Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketDetail() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { selectedGuild } = useGuild();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [updating, setUpdating] = useState(false);

    const fetchTicket = async () => {
        if (!ticketId || !token) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${API}/tickets/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTicket(response.data);
        } catch (error) {
            console.error('Error fetching ticket:', error);
            toast.error('Failed to load ticket');
            navigate('/dashboard/tickets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTicket();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, token]);

    const sendMessage = async () => {
        if (!message.trim()) return;

        setSending(true);
        try {
            await axios.post(`${API}/tickets/${ticketId}/messages`, {
                ticket_id: ticketId,
                user_id: user.discord_id,
                username: user.username,
                content: message,
                is_staff: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setMessage('');
            fetchTicket();
            toast.success('Message sent');
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const updateTicket = async (updates) => {
        setUpdating(true);
        try {
            await axios.patch(`${API}/tickets/${ticketId}`, updates, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTicket();
            toast.success('Ticket updated');
        } catch (error) {
            console.error('Error updating ticket:', error);
            toast.error('Failed to update ticket');
        } finally {
            setUpdating(false);
        }
    };

    const deleteTicket = async () => {
        try {
            await axios.delete(`${API}/tickets/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ticket deleted');
            navigate('/dashboard/tickets');
        } catch (error) {
            console.error('Error deleting ticket:', error);
            toast.error('Failed to delete ticket');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-500/20 text-red-500 border-red-500/30';
            case 'medium': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'low': return 'bg-green-500/20 text-green-500 border-green-500/30';
            default: return '';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-green-500/20 text-green-500 border-green-500/30';
            case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'closed': return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
            default: return '';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="ticket-detail-loading">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <Skeleton className="h-96" />
                    </div>
                    <Skeleton className="h-64" />
                </div>
            </div>
        );
    }

    if (!ticket) {
        return null;
    }

    return (
        <div className="space-y-6" data-testid="ticket-detail-page">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/dashboard/tickets')}
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                </Button>
                <h1 className="text-2xl font-bold truncate flex-1">{ticket.subject}</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Messages */}
                <div className="lg:col-span-2 space-y-4">
                    <Card data-testid="ticket-messages">
                        <CardHeader>
                            <CardTitle>Conversation</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4 max-h-[500px] overflow-y-auto">
                                {ticket.messages && ticket.messages.length > 0 ? (
                                    ticket.messages.map((msg, index) => (
                                        <motion.div
                                            key={msg.id || index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`flex gap-3 ${msg.is_staff ? 'flex-row-reverse' : ''}`}
                                        >
                                            <Avatar className="w-8 h-8">
                                                <AvatarFallback className={msg.is_staff ? 'bg-primary text-primary-foreground' : ''}>
                                                    {msg.username?.charAt(0).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className={`flex-1 ${msg.is_staff ? 'text-right' : ''}`}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium">{msg.username}</span>
                                                    {msg.is_staff && (
                                                        <Badge variant="outline" className="text-xs">Staff</Badge>
                                                    )}
                                                    <span className="text-xs text-muted-foreground">
                                                        {new Date(msg.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className={`rounded-lg p-3 ${msg.is_staff ? 'bg-primary/10 ml-auto' : 'bg-muted'} max-w-[80%] ${msg.is_staff ? 'ml-auto' : ''}`}>
                                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <p>No messages yet</p>
                                    </div>
                                )}
                            </div>

                            {/* Reply Box */}
                            {ticket.status !== 'closed' && (
                                <div className="mt-4 pt-4 border-t">
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Type your reply..."
                                            value={message}
                                            onChange={e => setMessage(e.target.value)}
                                            rows={3}
                                            className="flex-1"
                                            data-testid="reply-input"
                                        />
                                    </div>
                                    <div className="flex justify-end mt-2">
                                        <Button 
                                            onClick={sendMessage}
                                            disabled={sending || !message.trim()}
                                            data-testid="send-reply-btn"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            {sending ? 'Sending...' : 'Send Reply'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Ticket Info */}
                <div className="space-y-4">
                    <Card data-testid="ticket-info">
                        <CardHeader>
                            <CardTitle>Ticket Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Status</span>
                                <Select
                                    value={ticket.status}
                                    onValueChange={value => updateTicket({ status: value })}
                                    disabled={updating}
                                >
                                    <SelectTrigger className="w-32" data-testid="status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="open">Open</SelectItem>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="closed">Closed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Priority</span>
                                <Select
                                    value={ticket.priority}
                                    onValueChange={value => updateTicket({ priority: value })}
                                    disabled={updating}
                                >
                                    <SelectTrigger className="w-32" data-testid="priority-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="high">High</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="low">Low</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Category</span>
                                <Badge variant="outline">{ticket.category}</Badge>
                            </div>
                            
                            <div className="pt-4 border-t space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Created by:</span>
                                    <span>{ticket.username}</span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-sm">
                                    <Clock className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Created:</span>
                                    <span>{new Date(ticket.created_at).toLocaleString()}</span>
                                </div>
                                
                                {ticket.closed_at && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-muted-foreground">Closed:</span>
                                        <span>{new Date(ticket.closed_at).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Actions</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {ticket.status !== 'closed' ? (
                                <Button 
                                    variant="outline" 
                                    className="w-full justify-start"
                                    onClick={() => updateTicket({ status: 'closed' })}
                                    disabled={updating}
                                    data-testid="close-ticket-btn"
                                >
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Close Ticket
                                </Button>
                            ) : (
                                <Button 
                                    variant="outline" 
                                    className="w-full justify-start"
                                    onClick={() => updateTicket({ status: 'open' })}
                                    disabled={updating}
                                    data-testid="reopen-ticket-btn"
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reopen Ticket
                                </Button>
                            )}
                            
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button 
                                        variant="outline" 
                                        className="w-full justify-start text-destructive hover:text-destructive"
                                        data-testid="delete-ticket-btn"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete Ticket
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Ticket?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            This action cannot be undone. This will permanently delete the ticket and all its messages.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={deleteTicket} className="bg-destructive text-destructive-foreground">
                                            Delete
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
